import React, { createContext, useMemo } from "react";
import { APIContext } from "./api_hook";
import { Test } from "../components/global/VideoCard";

// Type definitions
interface Video {
  video_id: string;
  title: string;
  imgLink: string;

  [key: string]: any;
}

interface VideoContextValue {
  videos: Video[];
  getVideo: (id: string | null) => Promise<{ video: Video[], count: number }>;
  getVideos: (search_query: string | null, page: number | null) => Promise<{ videos: Video[], count: number }>;
  updateVideosFromChannel: () => Promise<any>;
  results_per_page: number;
  setSelectedVideo: React.Dispatch<React.SetStateAction<string | null>>;
  selectedVideo: Video | undefined;
  alignment: string | null;
  setAlignment: React.Dispatch<React.SetStateAction<string | null>>;
  titles: string[];
  setTitles: React.Dispatch<React.SetStateAction<string[]>>;
  files: File[];
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  useOriginal: boolean;
  setUseOriginal: React.Dispatch<React.SetStateAction<boolean>>;
  createTest: () => Promise<any>;
  duration: number;
  tests: Test[];
  loading: boolean;
  suggestedTitles: string[];
  setSuggestedTitles: React.Dispatch<React.SetStateAction<string[]>>;
  suggestedMoods: string[];
  setSuggestedMoods: React.Dispatch<React.SetStateAction<string[]>>;
  getSuggestedTitles: (mood?: string) => Promise<any>;
  suggestionsLoading: boolean;
}

type VideoContextType = {
  children: React.ReactNode;
};

// Creating the context
export const VideoContext = createContext<VideoContextValue | null>(null);

// Utility functions
const blobToBase64Async = async (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.onerror = (e) => reject(fileReader.error);
    fileReader.onloadend = (e) => {
      const dataUrl = fileReader.result as string;
      // remove "data:mime/type;base64," prefix from data url
      const base64 = dataUrl.substring(dataUrl.indexOf(",") + 1);
      resolve(base64);
    };
    fileReader.readAsDataURL(blob);
  });
};

const getContentType = (filetype: string): string => {
  if (filetype === "png") return "image/png";
  if (filetype === "jpeg") return "image/jpeg";
  if (filetype === "jpg") return "image/jpg";
  if (filetype === "gif") return "image/gif";
  return "";
};

const VideoProvider = (props: VideoContextType) => {
  const results_per_page = 20;
  const { children } = props;
  const { fetch_api } = React.useContext(APIContext);

  // State management
  const [videos, setVideos] = React.useState<Video[]>([]);
  const [selectedVideoID, setSelectedVideo] = React.useState<string | null>(null);
  const [files, setFiles] = React.useState<File[]>([]);
  const [useOriginal, setUseOriginal] = React.useState<boolean>(true);
  const [alignment, setAlignment] = React.useState<string | null>("1day");
  const [loading, setLoading] = React.useState<boolean>(false);
  const [titles, setTitles] = React.useState<string[]>([]);
  // Add a dedicated state for tests instead of computing it on every render
  const [tests, setTests] = React.useState<Test[]>([]);
  const [suggestedTitles, setSuggestedTitles] = React.useState<string[]>([]);
  const [suggestedMoods, setSuggestedMoods] = React.useState<string[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = React.useState<boolean>(false);

  // Derived state
  const selectedVideo = videos.find(
    (video: Video) => video.video_id === selectedVideoID
  );

  // Duration options in seconds — day-aligned to match YouTube Analytics' Pacific Time day boundaries.
  // Sub-daily options hidden; uncomment here and in TestOptions.tsx to re-enable:
  // "1min": 60, "5min": 300, "1hour": 3600, "6hours": 21600,
  const DURATIONS = {
    "1day": 86400,
    "2days": 172800,
    "3days": 259200,
    "1week": 604800,
    "2weeks": 1209600,
  };
  const duration = alignment ? DURATIONS[alignment as keyof typeof DURATIONS] : DURATIONS["1day"];

  // API functions
  const getVideos = async (
    search_query: string | null,
    page: number | null
  ) => {
    let query_params = {
      results_per_page: results_per_page.toString(),
      search_query: "",
      page: ""
    };

    if (search_query !== null && search_query.toString().trim() !== "") {
      query_params = { ...query_params, search_query };
    }
    if (page !== null) {
      query_params = { ...query_params, page: page.toString() };
    }

    setLoading(true);
    const options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      }
    };
    const resp = await fetch_api(
      "/api/videos/?" + new URLSearchParams(query_params),
      options
    );
    setLoading(false);
    setVideos(resp?.videos || []);
    return { videos: resp?.videos, count: resp?.count };
  };

  const getVideo = async (id: string | null) => {
    let query_params = {
      id: ""
    };

    if (id !== null && id.toString().trim() !== "") {
      console.log("searching");
      query_params = { ...query_params, id };
    }

    setLoading(true);
    const options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      }
    };
    const resp = await fetch_api(
      "/api/video/" + new URLSearchParams(query_params),
      options
    );
    setLoading(false);
    setVideos(resp?.video || []);
    return { video: resp?.video, count: resp?.count };
  };

  const updateVideosFromChannel = async () => {
    setLoading(true);
    const options = {
      method: "GET"
    };
    const resp = await fetch_api("/api/update_videos_in_database/", options);
    setLoading(false);
    return resp;
  };

  const getSuggestedTitles = async (mood?: string) => {
    if (!selectedVideo) return;

    setSuggestionsLoading(true);
    const body: any = {
      video_id: selectedVideo.video_id,
      current_title: selectedVideo.title || "",
      description: selectedVideo.description || "",
      user_titles: titles
    };

    // Add mood parameter if provided
    if (mood) {
      body.mood = mood;
    }

    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(body)
    };

    try {
      const resp = await fetch_api("/api/suggest_titles/", options);
      if (resp?.success && resp?.suggested_titles) {
        setSuggestedTitles(resp.suggested_titles);
        // Only update moods on initial request (when no mood is specified)
        if (!mood && resp?.suggested_moods) {
          setSuggestedMoods(resp.suggested_moods);
        }
      }
      setSuggestionsLoading(false);
      return resp;
    } catch (error) {
      console.error("Error fetching suggested titles:", error);
      setSuggestionsLoading(false);
      throw error;
    }
  };

  // Test generation functions
  const prepareTestsWithFiles = async (tests: Test[]): Promise<any[]> => {
    return Promise.all(
      tests.map(async (test: Test) => {
        if (test.loadFromImgLink) {
          return test;
        }

        const filetype = test.filename.split(".").pop() || "";
        const contentType = getContentType(filetype);

        let file = await fetch(test.imgLink, {
          method: "GET"
        })
          .then((r) => r.blob())
          .then((blobFile) => blobToBase64Async(blobFile));

        return { ...test, file, contentType };
      })
    );
  };

  // Generate tests using useMemo to prevent infinite loops
  // This will only recompute when dependencies change
  const generateTests = useMemo(() => {
    const generatedTests: Test[] = [];
    let ind = 0;

    for (const title of titles) {
      if (useOriginal && selectedVideo) {
        generatedTests.push({
          imgLink: selectedVideo.imgLink + "?t=" + Math.floor(Math.random() * 1000),
          filename: selectedVideo.imgLink.split("/").pop() as string,
          title: title,
          loadFromImgLink: true,
          order: ind,
          duration: duration,
          video_id: selectedVideo.video_id
        });
        ind = ind + 1;
      }
      for (const file of files) {
        generatedTests.push({
          imgLink: URL.createObjectURL(file),
          filename: file.name,
          title: title,
          order: ind,
          duration: duration,
          video_id: selectedVideo ? selectedVideo.video_id : ""
        });
        ind = ind + 1;
      }
    }

    return generatedTests;
  }, [titles, useOriginal, selectedVideo, files, duration]);

  // Update tests state when generateTests output changes
  React.useEffect(() => {
    setTests(generateTests);
  }, [generateTests]);

  // Effects
  React.useEffect(() => {
    const sVideo = videos.find(
      (video: Video) => video.video_id === selectedVideoID
    );
    if (sVideo && sVideo.title) {
      setTitles([sVideo.title]);
    }
  }, [selectedVideoID, videos]);

  // Modified createTest function
  const createTest = async () => {
    if (!selectedVideo) return;

    setLoading(true);

    // Use the current tests from state instead of regenerating
    const tests_with_files = await prepareTestsWithFiles(tests);

    const body = {
      tests: tests_with_files,
      video_id: selectedVideo.video_id
    };

    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(body)
    };

    const resp = await fetch_api("/api/create_test/", options);
    setLoading(false);
    return resp;
  };

  return (
    <VideoContext.Provider
      value={{
        videos,
        getVideo,
        getVideos,
        updateVideosFromChannel,
        results_per_page,
        setSelectedVideo,
        selectedVideo,
        alignment,
        setAlignment,
        titles,
        setTitles,
        files,
        setFiles,
        useOriginal,
        setUseOriginal,
        createTest,
        duration,
        tests,
        loading,
        suggestedTitles,
        setSuggestedTitles,
        suggestedMoods,
        setSuggestedMoods,
        getSuggestedTitles,
        suggestionsLoading
      }}
    >
      {children}
    </VideoContext.Provider>
  );
};

export default VideoProvider;